/**
 * HeroTrouveChat — homepage hero chat that leads with Trouvé.
 *
 * Iter 79 (Jun 25 2026) brief from the founder:
 *   Lead the homepage hero with an interactive Trouvé chat. Open with
 *   "Hey, I'm Trouvé. Let's talk beauty. What's on your mind today?"
 *   Show 6 soft pill chips for common topics. Keep a free-text input
 *   visible. After 2 lightweight turns, gate signup via Create my
 *   free account → /login → /onboarding (with intent preloaded so
 *   the goal question is skipped).
 *
 * UX state machine:
 *   turn0  → trouvé opener + 6 topic chips + free-text input
 *   turn1  → trouvé acknowledgement + 3 mini chips + free-text input
 *   turn2  → trouvé tip (1-2 sentences) + Create my free account CTA
 *
 * Chip taps are INSTANT (templates from heroTrouveTemplates.js).
 * Free-text only hits /api/demo/trouve/ask (LLM) when the user types
 * — and even then we keep the reply short and pivot to a chip set for
 * the next turn so we never stall on slow LLM responses.
 *
 * Accessibility: every interactive element has data-testid + an aria
 * label. The chip row uses role="listbox" + role="option" so screen
 * readers announce it as a single decision surface. The transcript
 * scrolls to the latest bubble on every turn.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Send, Sparkles } from "lucide-react";

import { trackFP } from "../lib/firstPartyAnalytics";
import { saveHeroIntent } from "../lib/heroIntent";
import {
  HERO_TROUVE_TOPICS,
  HERO_TROUVE_TURN1,
  HERO_TROUVE_TURN2,
  topicToGoal,
} from "../lib/heroTrouveTemplates";

// Tiny Trouvé brand mark — soft-pink filled circle with a sparkle
// glyph. Keeps the hero light; we don't ship a photo here because the
// founder's portrait already lives next to this chat.
const TrouveAvatar = () => (
  <div
    aria-hidden="true"
    className="shrink-0 h-9 w-9 rounded-full bg-gradient-to-br from-[#F5C7C5] to-[#9B6A6C] flex items-center justify-center shadow-[0_3px_10px_rgba(155,106,108,0.35)]"
  >
    <Sparkles className="h-4 w-4 text-white" />
  </div>
);

// Pretty time-out so the user can read Trouvé's response before the
// next chip set appears. Long enough to feel composed, short enough
// to not feel laggy.
const REVEAL_DELAY_MS = 280;

const TrouveBubble = ({ text, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, ease: "easeOut" }}
    className="flex items-start gap-2.5"
  >
    <TrouveAvatar />
    <div
      className="max-w-[78%] sm:max-w-[82%] rounded-2xl rounded-tl-sm bg-[#FAF6F2] border border-[#EADDD7] px-4 py-2.5 text-[14px] leading-relaxed text-[#2C2123] whitespace-pre-line"
      data-testid={testid}
    >
      {text}
    </div>
  </motion.div>
);

const UserBubble = ({ text, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, ease: "easeOut" }}
    className="flex items-start gap-2.5 justify-end"
  >
    <div
      className="max-w-[78%] sm:max-w-[82%] rounded-2xl rounded-tr-sm bg-[#9B6A6C] px-4 py-2.5 text-[14px] leading-relaxed text-white shadow-[0_3px_10px_rgba(155,106,108,0.25)]"
      data-testid={testid}
    >
      {text}
    </div>
  </motion.div>
);

const ChipRow = ({ chips, onPick, testid, variant = "wrap" }) => {
  // `variant="grid"` lays the chips out in a compact 2-col (mobile)
  // / 3-col (sm+) responsive grid — used for the 6 Turn-0 topic
  // chips so the hero stays tight above the fold on mobile.
  // `variant="wrap"` (default) keeps the original flex-wrap behavior
  // for short chip sets (e.g. the 3 Turn-1 mini chips) where a grid
  // would just stretch them awkwardly.
  const containerClass =
    variant === "grid"
      ? "grid grid-cols-2 sm:grid-cols-3 gap-2"
      : "flex flex-wrap gap-2";
  return (
    <div
      className={containerClass}
      role="listbox"
      aria-label="Topic chips"
      data-testid={testid}
    >
      {chips.map((chip) => (
        <button
          key={chip.id}
          type="button"
          role="option"
          aria-selected="false"
          onClick={() => onPick(chip)}
          className="inline-flex items-center justify-center text-center gap-1.5 rounded-2xl border border-[#EADDD7] bg-white px-2.5 py-2 sm:px-3.5 sm:py-2 text-[12px] sm:text-[13px] leading-tight text-[#2C2123] hover:border-[#9B6A6C] hover:bg-[#FAF6F2] hover:-translate-y-0.5 transition-all duration-200 touch-manipulation min-h-[40px]"
          data-testid={`${testid}-${chip.id}`}
        >
          {chip.emoji && (
            <span aria-hidden="true" className="text-base leading-none shrink-0">
              {chip.emoji}
            </span>
          )}
          <span className="whitespace-normal break-words">{chip.label}</span>
        </button>
      ))}
    </div>
  );
};

const HeroTrouveChat = ({ onSkip }) => {
  // `onSkip` (optional) — invoked with no arguments when the user taps
  // the "or skip the chat and start your free routine" secondary link.
  // The parent is expected to handle session-mint + navigation on its
  // own; HeroTrouveChat does NOT pass an event object (the link is a
  // plain `<button>`, not a `<Link>`, so there's no default behaviour
  // to suppress). Parent handlers that previously assumed `e` was
  // present must guard with `e?.preventDefault?.()`.
  const { t } = useTranslation();
  const navigate = useNavigate();

  // turn 0 → 1 → 2 → "gate" (the signup CTA state).
  const [phase, setPhase] = useState("turn0");
  const [transcript, setTranscript] = useState([
    { role: "trouve", id: "opener", text: t("heroTrouve.opener") },
  ]);
  // selectedTopic locks in once the user picks a Turn 0 chip OR submits
  // freeform text. Stored so we can compute the Turn 1 chip set and
  // know which Turn 2 tip to render.
  const [selectedTopic, setSelectedTopic] = useState(null);
  // Free-text state for the bottom input. Cleared on each submit so
  // the user can do back-to-back turns without manually wiping.
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  // Track turn 1 mini-chip selection so the saved hero-intent blob
  // carries it through to Onboarding for richer routine generation.
  const [turn1ChipId, setTurn1ChipId] = useState(null);

  const scrollRef = useRef(null);

  // Auto-scroll on every transcript update so the latest bubble is in
  // view. Smooth scroll feels more like a real chat than instant jump.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [transcript]);

  // Persist the captured intent. Called once we've committed to a
  // topic (Turn 0 pick or free-text submit). Onboarding picks it up.
  const persistIntent = useCallback(
    (extra = {}) => {
      const t0 = selectedTopic;
      if (!t0) return;
      saveHeroIntent({
        topic_id: t0.id,
        topic_label: t0.label,
        turn1_chip_id: extra.turn1_chip_id ?? turn1ChipId ?? null,
        turn1_chip_label: extra.turn1_chip_label ?? null,
        free_text: extra.free_text ?? null,
        transcript: transcript.map((m) => ({ role: m.role, text: m.text })),
        captured_at: new Date().toISOString(),
        goal: topicToGoal(t0.id),
      });
    },
    [selectedTopic, turn1ChipId, transcript],
  );

  // Push a Trouvé bubble after a short delay (feels less robotic).
  const pushTrouve = useCallback((id, text) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        setTranscript((prev) => [...prev, { role: "trouve", id, text }]);
        resolve();
      }, REVEAL_DELAY_MS);
    });
  }, []);

  const handleTopicPick = useCallback(
    async (topic) => {
      // Turn 0 → Turn 1
      const userText = topic.label;
      setTranscript((prev) => [
        ...prev,
        { role: "user", id: `u-topic-${topic.id}`, text: userText },
      ]);
      setSelectedTopic(topic);
      trackFP("hero_trouve_topic_selected", {
        topic_id: topic.id,
        topic_label: topic.label,
      });
      const turn1 = HERO_TROUVE_TURN1[topic.id];
      if (turn1) {
        await pushTrouve(`t1-${topic.id}`, t(turn1.bodyKey));
      }
      setPhase("turn1");
    },
    [pushTrouve, t],
  );

  const handleTurn1ChipPick = useCallback(
    async (chip) => {
      // Turn 1 → Turn 2 (final tip + signup gate)
      const userText = chip.label;
      setTranscript((prev) => [
        ...prev,
        { role: "user", id: `u-mini-${chip.id}`, text: userText },
      ]);
      setTurn1ChipId(chip.id);
      const tip = HERO_TROUVE_TURN2[selectedTopic?.id];
      if (tip) {
        await pushTrouve(`t2-${selectedTopic.id}`, t(tip));
      }
      trackFP("hero_trouve_turn_2_completed", {
        topic_id: selectedTopic?.id || null,
        turn1_chip_id: chip.id,
      });
      // Persist BEFORE the user clicks the signup CTA so even a tab
      // close + reload while the gate is showing doesn't drop intent.
      persistIntent({
        turn1_chip_id: chip.id,
        turn1_chip_label: chip.label,
      });
      setPhase("gate");
    },
    [pushTrouve, t, selectedTopic, persistIntent],
  );

  const handleFreeTextSubmit = useCallback(async () => {
    const text = draft.trim();
    if (!text) return; // graceful empty-input handling (no error toast)
    if (sending) return;

    // Push the user bubble immediately so the chat feels responsive.
    setTranscript((prev) => [
      ...prev,
      { role: "user", id: `u-text-${Date.now()}`, text },
    ]);
    setDraft("");
    setSending(true);

    if (phase === "turn0") {
      // Free-text on Turn 0: hit the LLM endpoint for a single warm
      // acknowledgment + follow-up. We default the topic to a synthetic
      // "freeform" id and surface 3 generic chips so we never block
      // the visitor on the LLM beyond the first turn.
      trackFP("hero_trouve_free_text_submitted", { turn: 0 });
      let reply = t("heroTrouve.freeform.fallbackTurn1");
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/demo/trouve/ask`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              message: text,
              context: { surface: "hero_freeform" },
            }),
          },
        );
        if (res.ok) {
          const data = await res.json();
          // Endpoint may return reply=null when quota is exhausted.
          // Treat null as "use fallback" — the user will still see
          // a graceful reply.
          if (data?.reply && typeof data.reply === "string") {
            reply = data.reply.slice(0, 320); // keep hero responses short
          }
        }
      } catch {
        // Silently fall through to the template reply — never error in
        // the visitor's face on the homepage.
      }
      setSelectedTopic({
        id: "freeform",
        label: text.slice(0, 60),
      });
      await pushTrouve(`t1-freeform-${Date.now()}`, reply);
      // Synthetic Turn 1 chips so the next interaction stays one tap.
      setPhase("turn1_freeform");
    } else if (phase === "turn1" || phase === "turn1_freeform") {
      // Free-text on Turn 1: skip the LLM (no benefit; we just need to
      // land the signup gate). Give the topic-specific tip if we have
      // one, else the generic gate copy.
      trackFP("hero_trouve_free_text_submitted", { turn: 1 });
      const tipKey = HERO_TROUVE_TURN2[selectedTopic?.id]
        || "heroTrouve.freeform.fallbackTurn2";
      await pushTrouve(`t2-text-${Date.now()}`, t(tipKey));
      trackFP("hero_trouve_turn_2_completed", {
        topic_id: selectedTopic?.id || "freeform",
        via: "free_text",
      });
      persistIntent({ free_text: text });
      setPhase("gate");
    }
    setSending(false);
  }, [draft, sending, phase, selectedTopic, pushTrouve, t, persistIntent]);

  const handleSignupCta = useCallback(() => {
    trackFP("hero_trouve_signup_cta_clicked", {
      topic_id: selectedTopic?.id || null,
    });
    // Persist one more time in case the user hit the CTA without
    // tapping a mini chip (e.g. via free-text path).
    persistIntent();
    navigate("/login?next=%2Fonboarding");
  }, [navigate, persistIntent, selectedTopic]);

  const handleSkip = useCallback(() => {
    trackFP("hero_trouve_skip_chat_clicked");
    if (onSkip) onSkip();
  }, [onSkip]);

  // Synthetic Turn 1 chips for the freeform path (when the user typed
  // their question instead of tapping a topic). 3 broad next-steps
  // that always make sense regardless of what they typed.
  const freeformTurn1Chips = [
    { id: "tell_me_more", label: t("heroTrouve.freeform.chip1") },
    { id: "build_routine", label: t("heroTrouve.freeform.chip2") },
    { id: "show_me_how", label: t("heroTrouve.freeform.chip3") },
  ];

  return (
    <div
      className="rounded-3xl border border-[#EADDD7] bg-white/95 backdrop-blur-sm shadow-[0_12px_36px_rgba(155,106,108,0.12)] overflow-hidden"
      data-testid="hero-trouve-chat"
    >
      {/* Transcript */}
      <div
        ref={scrollRef}
        className="px-3 sm:px-5 py-3 sm:py-5 space-y-2.5 sm:space-y-3 max-h-[340px] sm:max-h-[380px] overflow-y-auto"
      >
        <AnimatePresence initial={false}>
          {transcript.map((m) => (
            m.role === "trouve" ? (
              <TrouveBubble
                key={m.id}
                text={m.text}
                testid={`hero-trouve-bubble-${m.id}`}
              />
            ) : (
              <UserBubble
                key={m.id}
                text={m.text}
                testid={`hero-user-bubble-${m.id}`}
              />
            )
          ))}
        </AnimatePresence>

        {/* Turn 0 chips — visible immediately under the opener.
            Uses `variant="grid"` so the 6 topic chips lay out in a
            compact 2-col (mobile) / 3-col (sm+) grid that keeps the
            hero tight above the fold on phones. */}
        {phase === "turn0" && (
          <div className="pt-2">
            <ChipRow
              testid="hero-trouve-topics"
              variant="grid"
              chips={HERO_TROUVE_TOPICS.map((tp) => ({
                id: tp.id,
                emoji: tp.emoji,
                label: t(tp.labelKey, { defaultValue: tp.fallback }),
              }))}
              onPick={(picked) => {
                // Re-wrap the chip in the topic shape `handleTopicPick`
                // expects (id + label).
                handleTopicPick(picked);
              }}
            />
          </div>
        )}

        {/* Turn 1 chips (template path) */}
        {phase === "turn1" && selectedTopic && HERO_TROUVE_TURN1[selectedTopic.id] && (
          <div className="pt-2">
            <ChipRow
              testid="hero-trouve-mini-chips"
              chips={HERO_TROUVE_TURN1[selectedTopic.id].chips.map((c) => ({
                id: c.id,
                label: t(c.labelKey),
              }))}
              onPick={handleTurn1ChipPick}
            />
          </div>
        )}

        {/* Turn 1 chips (freeform path) */}
        {phase === "turn1_freeform" && (
          <div className="pt-2">
            <ChipRow
              testid="hero-trouve-mini-chips"
              chips={freeformTurn1Chips}
              onPick={(c) => {
                // Reuse the same handler — the freeform synthetic chip
                // doesn't have a topic-specific tip, so handleTurn1ChipPick
                // will fall through to the generic gate copy.
                handleTurn1ChipPick(c);
              }}
            />
          </div>
        )}

        {/* Signup gate (Turn 2 complete) */}
        {phase === "gate" && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="pt-2"
          >
            <button
              type="button"
              onClick={handleSignupCta}
              className="group inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-6 py-3 text-[14px] font-medium transition-all duration-300 shadow-[0_6px_18px_rgba(155,106,108,0.35)] hover:-translate-y-0.5 w-full sm:w-auto"
              data-testid="hero-trouve-signup-cta"
            >
              {t("heroTrouve.signupCta")}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </motion.div>
        )}
      </div>

      {/* Free-text input — visible in turn0 / turn1 / turn1_freeform.
          Hidden in `gate` because the CTA is the only thing we want
          the user clicking from there. */}
      {phase !== "gate" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleFreeTextSubmit();
          }}
          className="border-t border-[#EADDD7] bg-[#FDFBF7] px-3 py-2.5 flex items-center gap-2"
          data-testid="hero-trouve-input-form"
        >
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("heroTrouve.inputPlaceholder")}
            aria-label={t("heroTrouve.inputAriaLabel")}
            className="flex-1 bg-transparent text-[14px] text-[#2C2123] placeholder:text-[#9B6A6C]/70 focus:outline-none px-2 py-1.5"
            data-testid="hero-trouve-text-input"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={!draft.trim() || sending}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#9B6A6C] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#805557] transition-all"
            aria-label={t("heroTrouve.sendAriaLabel")}
            data-testid="hero-trouve-send-btn"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
      )}

      {/* Skip-chat secondary link. Renders in every phase so a
          visitor who already knows what TruBeauté is can bypass the
          chat without burying the option. */}
      {onSkip && (
        <button
          type="button"
          onClick={handleSkip}
          className="block w-full text-center text-[12px] text-[#5C4B51] hover:text-[#9B6A6C] underline-offset-4 hover:underline py-2.5 transition-colors"
          data-testid="hero-trouve-skip-link"
        >
          {t("heroTrouve.skipChat")}
        </button>
      )}
    </div>
  );
};

export default HeroTrouveChat;
